import { useEffect, useRef } from 'react';
import { wireShotPan } from './shot-pan';

/**
 * A screenshot from the guide capture harness, in both themes.
 *
 * The harness writes each shot as AVIF + WebP, dark under the plain name and
 * light under `<name>-light`. Both <picture>s are drawn and CSS hides the one
 * that does not match the theme (styles/components/guides.scss, keyed on the
 * `html.theme-*` and `.theme.light` classes that pages/_document.tsx sets
 * before first paint). So the right capture is on screen from the first frame,
 * with no flash and no script, and a lazy image that is display:none never
 * downloads -- the hidden theme costs nothing.
 *
 * The frame is the wrapper, not the image, so a wide, short strip can be
 * shown at its own size and panned inside it (shot-pan.ts).
 */
export default function GuideShot({ base, alt }: { base: string; alt: string }) {
  const frameRef = useRef<HTMLElement>(null);

  // The figure is always drawn, so the ref is set by the time this runs.
  useEffect(() => wireShotPan(frameRef.current), [base]);

  return (
    <figure ref={frameRef} className="guide-shot" data-guide-shot="">
      <picture className="guide-shot__pic guide-shot__pic--light">
        <source srcSet={`${base}-light.avif`} type="image/avif" />
        <img src={`${base}-light.webp`} alt={alt} loading="lazy" decoding="async" className="guide-shot__img" />
      </picture>
      <picture className="guide-shot__pic guide-shot__pic--dark">
        <source srcSet={`${base}.avif`} type="image/avif" />
        <img src={`${base}.webp`} alt={alt} loading="lazy" decoding="async" className="guide-shot__img" />
      </picture>
    </figure>
  );
}
